/**
 * Everything a script is allowed to see, built in one place.
 *
 * ## This function IS the data boundary
 *
 * A script written by a stranger runs inside an application that also holds the
 * trader's journal, their broker session, their positions and their billing. It
 * sees exactly what this function puts in the object it returns, and nothing
 * else — the Worker it runs in has no other way to reach any of it.
 *
 * So the object is built by hand, field by field, from narrow arguments. It is
 * never spread from a larger object, never `JSON.parse(JSON.stringify(state))`,
 * and never handed a record that has other fields on it. Any of those would
 * make the boundary depend on what somebody else's type happens to contain
 * today, and the day a `userId` is added to that type is the day it silently
 * crosses into a stranger's code.
 *
 * `script-payload.test.ts` walks the result recursively and fails on any key
 * outside an exhaustive list. That test is the guard; this comment is only the
 * reason for it.
 *
 * ## Numbers, not Decimals
 *
 * Prices cross as JS numbers. Two reasons, and one consequence.
 *
 * A `Decimal` is a class instance, and structured clone — the algorithm that
 * copies data into a Worker — does not carry class instances. It would arrive
 * as a bare object with no methods.
 *
 * And it does not need to be exact: this is chart maths. `money.ts` already
 * draws the line — cash and stored prices are Decimal, indicator and pixel
 * maths are numbers, because they are display concerns.
 *
 * The consequence, which is a product rule rather than a technical one: a
 * script's output is a DRAWING and must never reach a cash figure. Nothing in
 * the journal, the P&L, the R-multiple or the rule engine reads a plot, and
 * nothing should be built that does.
 */
import type { Bar } from '../journal/types.js';
import { dec, type DecimalLike } from '../money.js';
import { MAX_SCRIPT_BARS } from './types.js';
import type { ScriptInputValue } from './manifest.js';

/**
 * The bars, transposed into parallel arrays.
 *
 * Column-major rather than an array of objects. 5,000 bars is six typed-ish
 * arrays instead of 5,000 small objects to clone, and it is also the shape
 * indicator code wants: `close[i - 1]` with no property lookup, and
 * `close.slice(-length)` for a window.
 */
export interface ScriptBars {
  /** Epoch SECONDS at bar open, ascending. Seconds, matching `Bar`. */
  readonly time: readonly number[];
  readonly open: readonly number[];
  readonly high: readonly number[];
  readonly low: readonly number[];
  readonly close: readonly number[];
  readonly volume: readonly number[];
}

/**
 * What a script knows about the contract.
 *
 * Enough to express a level in ticks, which is the one thing an indicator
 * genuinely cannot compute for itself and must not guess — a hard-coded tick
 * size is wrong for every instrument but one.
 *
 * Not the canonical symbol, not the exchange, not the expiry. `root` is here
 * because an author writing a script for a specific contract needs to be able
 * to tell; the rest would only widen what a shared script can fingerprint about
 * the person running it.
 */
export interface ScriptInstrument {
  readonly root: string;
  readonly tickSize: number;
  readonly tickValue: number;
  readonly pointValue: number;
  readonly precision: number;
}

/**
 * The five fields the payload is built FROM.
 *
 * Deliberately narrower than `Instrument`. The builder cannot read what it was
 * not given, so a field added to the instrument master — or to the API's
 * instrument DTO — cannot reach a script by being carried along in an argument
 * that happened to be wide enough.
 *
 * `DecimalLike` rather than `Decimal`, so the same function serves the server,
 * which holds exact decimals, and the client, which receives them as strings.
 */
export interface ScriptInstrumentSource {
  readonly root: string;
  readonly tickSize: DecimalLike;
  readonly tickValue: DecimalLike;
  readonly pointValue: DecimalLike;
  readonly displayPrecision: number;
}

/** The complete argument to one run. Nothing reaches a script except this. */
export interface ScriptPayload {
  readonly bars: ScriptBars;
  readonly instrument: ScriptInstrument;
  readonly inputs: Readonly<Record<string, ScriptInputValue>>;
}

/**
 * Build the payload for one run.
 *
 * Bars are trimmed to the most recent `MAX_SCRIPT_BARS` rather than refused.
 * The cap bounds the clone and the script's own allocations; a chart holding
 * more history than that is ordinary, and refusing to draw an indicator because
 * the trader scrolled back too far would be a bug they could not act on.
 *
 * Non-finite values are not repaired here. A bar with a NaN close is a data
 * problem upstream, and quietly substituting a number would put an invented
 * price in front of a trader.
 */
export function buildScriptPayload(
  bars: readonly Bar[],
  instrument: ScriptInstrumentSource,
  inputs: Readonly<Record<string, ScriptInputValue>>,
): ScriptPayload {
  const used = bars.length > MAX_SCRIPT_BARS ? bars.slice(-MAX_SCRIPT_BARS) : bars;
  const n = used.length;

  const time = new Array<number>(n);
  const open = new Array<number>(n);
  const high = new Array<number>(n);
  const low = new Array<number>(n);
  const close = new Array<number>(n);
  const volume = new Array<number>(n);

  for (let i = 0; i < n; i += 1) {
    const b = used[i]!;
    time[i] = b.time;
    open[i] = b.open;
    high[i] = b.high;
    low[i] = b.low;
    close[i] = b.close;
    volume[i] = b.volume;
  }

  /* Copied key by key. See the file header — a spread here would be the bug. */
  const copied: Record<string, ScriptInputValue> = {};
  for (const [k, v] of Object.entries(inputs)) copied[k] = v;

  return {
    bars: { time, open, high, low, close, volume },
    instrument: {
      root: instrument.root,
      tickSize: dec(instrument.tickSize).toNumber(),
      tickValue: dec(instrument.tickValue).toNumber(),
      pointValue: dec(instrument.pointValue).toNumber(),
      precision: instrument.displayPrecision,
    },
    inputs: copied,
  };
}
